import { withGitpodSize } from "@/assets/icons/utils";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { InputField } from "@/components/podkit/forms/InputField";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { TrackLocations } from "@/hooks/use-segment";
import {
    useCanDeleteWebhook,
    useDeleteWebhook,
    useGetWebhookSecret,
    useRotateWebhookSecret,
    type PlainWebhook,
} from "@/queries/webhook-queries";
import { IconArrowRotateLeftRight } from "central-icons/IconArrowRotateLeftRight";
import type { FC } from "react";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { Banner } from "@/components/flexkit/Banner";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { WebhookProvider } from "gitpod-next-api/gitpod/v1/webhook_pb";

type WebhookDetailsDialogProps = {
    webhook?: PlainWebhook;
    open: boolean;
    onClose: () => void;
    isLoading?: boolean;
};

const PROVIDER_CONFIG = {
    [WebhookProvider.GITHUB]: {
        name: "GitHub",
        docsUrl: "https://docs.github.com/en/developers/webhooks-and-events/webhooks/creating-webhooks",
        showContentType: true,
    },
    [WebhookProvider.GITLAB]: {
        name: "GitLab",
        docsUrl: "https://docs.gitlab.com/ee/user/project/integrations/webhooks.html",
        showContentType: false,
    },
    [WebhookProvider.UNSPECIFIED]: {
        name: "SCM provider",
        docsUrl: "",
        showContentType: false,
    },
};

export const WebhookDetailsDialog: FC<WebhookDetailsDialogProps> = ({ open, onClose, webhook, isLoading }) => {
    const { toast } = useToast();
    const { data: secretToken, isLoading: isLoadingSecret } = useGetWebhookSecret(webhook?.id);
    const { mutateAsync: rotateSecret, isPending: isRotating } = useRotateWebhookSecret();
    const { mutateAsync: deleteWebhook, isPending: isDeleting } = useDeleteWebhook();
    const { canDeleteWebhook } = useCanDeleteWebhook(webhook?.id);

    const provider = webhook?.spec?.provider ?? WebhookProvider.UNSPECIFIED;
    const providerConfig = PROVIDER_CONFIG[provider];
    const payloadUrl = webhook?.url || "";
    const contentType = "application/json";

    const handleRotateSecret = async () => {
        if (!webhook) return;
        try {
            await rotateSecret(webhook.id);
            toast({
                title: "Webhook secret has been rotated",
            });
        } catch (error) {
            toast({
                title: "Failed to rotate webhook secret",
                description: formatError(error),
            });
        }
    };

    const handleDelete = async () => {
        if (!webhook) return;
        try {
            await deleteWebhook(webhook.id);
            toast({
                title: "Webhook deleted",
            });
            onClose();
        } catch (error) {
            toast({
                title: "Failed to delete webhook",
                description: formatError(error),
            });
        }
    };

    const webhookName = webhook?.metadata?.name || "Webhook";
    const webhookScope = webhook?.spec?.scope || "";

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                data-testid="webhook-details-dialog"
                data-track-location={TrackLocations.ConfigureWebhookDialog}
            >
                <DialogHeader className="mb-2">
                    <DialogTitle>{isLoading ? "Loading..." : webhookName}</DialogTitle>
                    <DialogDescription>
                        {isLoading
                            ? "Loading webhook details"
                            : webhookScope
                              ? `Configure webhook for ${webhookScope}`
                              : "Configure webhook settings"}
                    </DialogDescription>
                </DialogHeader>
                <DialogBody className="flex flex-col gap-4 px-0">
                    {isLoading ? (
                        <LoadingState />
                    ) : (
                        <>
                            <Banner
                                variant="info"
                                text={`Copy the Payload URL and Secret Token into your ${providerConfig.name} repository's webhook settings.`}
                                link={
                                    providerConfig.docsUrl ? (
                                        <ExternalLink
                                            href={providerConfig.docsUrl}
                                            className="text-sm font-medium text-content-primary"
                                        >
                                            Learn more
                                        </ExternalLink>
                                    ) : undefined
                                }
                            />
                            <div className="flex flex-col gap-4">
                                <InputField
                                    label="Payload URL"
                                    hint="Use this endpoint to receive events from your SCM provider."
                                    id="payload-url"
                                >
                                    <CopyableInput
                                        id="payload-url"
                                        value={payloadUrl}
                                        data-testid="payload-url-input"
                                        data-tracking-id="copy-payload-url-webhook-details-dialog"
                                    />
                                </InputField>
                                {providerConfig.showContentType && (
                                    <InputField label="Content Type">
                                        <CopyableInput
                                            value={contentType}
                                            readOnly
                                            data-testid="content-type-input"
                                            data-tracking-id="copy-content-type-webhook-dialog"
                                        />
                                    </InputField>
                                )}
                                <InputField
                                    label="Secret token"
                                    hint="Use this token to verify webhook signatures."
                                    id="secret-token"
                                >
                                    <div className="flex items-center gap-2">
                                        <CopyableInput
                                            id="secret-token"
                                            type="password"
                                            value={secretToken || ""}
                                            disabled={isLoadingSecret}
                                            data-testid="secret-token-input"
                                            data-tracking-id="copy-secret-token-webhook-details-dialog"
                                            wrapperClassName="grow"
                                        />
                                        <Button
                                            variant="outline"
                                            onClick={handleRotateSecret}
                                            disabled={isRotating || isLoadingSecret}
                                            loading={isRotating}
                                            data-testid="rotate-secret-button"
                                            data-tracking-id="rotate-secret-webhook-details-dialog"
                                            LeadingIcon={IconArrowRotateLeftRightWithSize}
                                        />
                                    </div>
                                </InputField>
                            </div>
                        </>
                    )}
                </DialogBody>
                {!isLoading && (
                    <DialogFooter>
                        <Button
                            variant="destructive-secondary"
                            onClick={handleDelete}
                            disabled={isDeleting || !canDeleteWebhook}
                            loading={isDeleting}
                            data-testid="delete-webhook-button"
                            data-tracking-id="delete-webhook-details-dialog"
                        >
                            Delete
                        </Button>
                        <DialogClose asChild>
                            <Button variant="outline" data-testid="done-webhook-button">
                                Done
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
};

const IconArrowRotateLeftRightWithSize = withGitpodSize(IconArrowRotateLeftRight);
